import { LEAD_STAGES, type LeadStageKey } from '@crm/shared';
import { cn } from '@crm/ui';
import {
  ArrowRightLeft,
  Bot,
  CalendarCheck,
  type LucideIcon,
  MessagesSquare,
  Send,
  UserPlus,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

/** Cada etapa com o seu simbolo: em um menu, o icone se le antes do texto. */
const ICONE_DA_ETAPA: Record<LeadStageKey, LucideIcon> = {
  MESSAGE_SENT: Send,
  LEAD: UserPlus,
  AUTO_CONTACT: Bot,
  HUMAN_CONTACT: MessagesSquare,
  SCHEDULED: CalendarCheck,
};

interface MoveMenuProps {
  /** Etapa atual - ela nao entra na lista, ja e a coluna onde o card esta. */
  stage: LeadStageKey;
  leadName: string;
  onMove: (stage: LeadStageKey) => void;
}

/** Mudar a etapa do lead em um toque, no proprio card. */
export function MoveMenu({ stage, leadName, onMove }: MoveMenuProps) {
  const [isOpen, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const botaoRef = useRef<HTMLButtonElement>(null);

  // Clique fora e Esc fecham, como em qualquer menu do sistema.
  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      setOpen(false);
      botaoRef.current?.focus();
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  /** Aberto, o primeiro item ja recebe o foco - e o que faz as setas andarem. */
  useEffect(() => {
    if (!isOpen) return;

    menuRef.current?.querySelector('button')?.focus();
  }, [isOpen]);

  function andarComSetas(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    event.preventDefault();

    const itens = [...(menuRef.current?.querySelectorAll('button') ?? [])];
    const atual = itens.indexOf(document.activeElement as HTMLButtonElement);
    const proximo = event.key === 'ArrowDown' ? atual + 1 : atual - 1;

    itens[(proximo + itens.length) % itens.length]?.focus();
  }

  return (
    <div ref={boxRef} className="relative shrink-0">
      <button
        ref={botaoRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={`Mover ${leadName} de etapa`}
        onClick={() => setOpen((open) => !open)}
        className={cn(
          'flex items-center gap-1 rounded py-1 text-xs font-medium text-slate-600 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:opacity-70 dark:text-slate-300',
          isOpen && 'text-foreground',
        )}
      >
        <ArrowRightLeft className="h-3.5 w-3.5" />
        Mover
      </button>

      {isOpen ? (
        <div
          ref={menuRef}
          role="menu"
          onKeyDown={andarComSetas}
          className="absolute right-0 z-30 mt-1 w-56 overflow-hidden rounded-xl border border-border bg-background shadow-xl ring-1 ring-black/5"
        >
          {LEAD_STAGES.filter((item) => item.key !== stage).map((item) => {
            const Icone = ICONE_DA_ETAPA[item.key];

            return (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onMove(item.key);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-muted focus:bg-muted focus:outline-none active:bg-muted/80 [&+&]:border-t [&+&]:border-border/50"
              >
                <Icone className="h-4 w-4 shrink-0 text-muted-foreground" />
                {item.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
